import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import {
  checkDuplicates,
  type DuplicateCheckRequest,
  type DuplicateCheckResponse,
} from '../api/checkDuplicates';

interface UseDuplicateCheckReturn {
  checkForDuplicates: (data: DuplicateCheckRequest) => Promise<DuplicateCheckResponse>;
  isChecking: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for checking potential duplicate leads before saving.
 *
 * @example
 * const { checkForDuplicates, isChecking } = useDuplicateCheck();
 *
 * const result = await checkForDuplicates({
 *   excludeLeadId: leadId, // when editing
 *   firstName,
 *   lastName,
 *   address1,
 *   // ... other fields
 * });
 *
 * if (result.hasDuplicates) {
 *   // Show warning dialog
 * }
 */
export function useDuplicateCheck(): UseDuplicateCheckReturn {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkForDuplicates = useCallback(
    async (data: DuplicateCheckRequest): Promise<DuplicateCheckResponse> => {
      setIsChecking(true);
      setError(null);

      try {
        const result = await checkDuplicates(data);
        return result;
      } catch (err) {
        const axiosError = err as AxiosError<{ message?: string }>;
        const errorMessage =
          axiosError.response?.data?.message || 'Failed to check for duplicates';
        setError(errorMessage);
        // Return no duplicates on error to allow form submission to proceed
        return { hasDuplicates: false, duplicates: [] };
      } finally {
        setIsChecking(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    checkForDuplicates,
    isChecking,
    error,
    clearError,
  };
}
